import { Fragment } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { yesNo, direction, fieldType, Flag } from '../../../../containers/common/constant/classification'
import { AddQuestion, GetCategoryMstDto, Option, SpecifyMaster } from '../../../../dataAccess/webApi/dto/categoryMstDto'
import { isEditable } from '../../../../containers/common/category'
import { GContainer, GItem } from '../../common/grids'
import { RadioButtonGroup } from '../../common/inputs/radioButtonGroup'
import { TextBox } from '../../common/inputs/textBox'
import { CheckBox } from '../../common/inputs/checkBox'
import { AttentionLabel } from '../../common/attentionLabel'

interface FlexibleCheckBoxesUnitProps {
  categoryMst: GetCategoryMstDto
  existFlagName?: string
  indentOff?: boolean
  hasUsagePermission?: boolean
}

export const FlexibleCheckBoxesUnit = (props: FlexibleCheckBoxesUnitProps) => {
  const { existFlagName, categoryMst, indentOff, hasUsagePermission} = props
  const methods = useFormContext()
  const existFlag = useWatch({ name: existFlagName ?? '', control: methods.control })
  const xsOutside = indentOff ? 0 : 0.3
  const xsInside = indentOff ? 12 : 11.7
  return (
    <>
      {existFlagName && categoryMst.checkExistence === yesNo.yes &&
        <GContainer >
          <RadioButtonGroup
            label={categoryMst.name}
            name={existFlagName}
            buttonValueLabels={[
              { label: categoryMst.checkTextNo ?? 'なし', value: yesNo.no },
              { label: categoryMst.checkTextYes ?? 'あり', value: yesNo.yes },
            ]}
            disabled={!isEditable(categoryMst.editCitizen, hasUsagePermission)}
            row
          />
        </GContainer>
      }
      {
        (categoryMst.checkExistence == null || categoryMst.checkExistence === Flag.OFF || existFlag === Flag.ON) && categoryMst?.options && (
          <GContainer>
            <GItem xs={xsOutside} />
            <GItem xs={xsInside} >
              {categoryMst.options.map((opt, i) =>
                <Fragment key={`${categoryMst.category}.${i}`} >
                  {categoryMst.direction === direction.horizontal ? (
                    <OptionForm
                      name={`${categoryMst.category}.${i}`}
                      option={opt}
                      disabled={!isEditable(categoryMst.editCitizen, hasUsagePermission)}
                    />
                  ) : (
                    <GContainer>
                      <OptionForm
                        name={`${categoryMst.category}.${i}`}
                        option={opt}
                        disabled={!isEditable(categoryMst.editCitizen, hasUsagePermission)}
                      />
                    </GContainer>
                  )}
                </Fragment>)}
            </GItem>
          </GContainer>
        )
      }
      {
        (categoryMst.checkExistence === Flag.OFF || existFlag === Flag.ON) && categoryMst?.masterData && (
          <GContainer>
            <GItem xs={xsOutside} />
            <GItem xs={xsInside} >
              {categoryMst.masterData.map((opt, i) =>
                <Fragment key={`${categoryMst.category}.${i}`}>
                  {categoryMst.direction === direction.horizontal ? (
                    <MasterDataForm
                      name={`${categoryMst.category}.${i}`}
                      masterData={opt}
                      disabled={!isEditable(categoryMst.editCitizen, hasUsagePermission)}
                    />
                  ) : (
                    <GContainer>
                      <MasterDataForm
                        name={`${categoryMst.category}.${i}`}
                        masterData={opt}
                        disabled={!isEditable(categoryMst.editCitizen, hasUsagePermission)}
                      />
                    </GContainer>
                  )}
                </Fragment>)}
            </GItem>
          </GContainer>
        )
      }
    </>
  )
}

interface OptionFormProps {
  name: string
  option: Option
  disabled: boolean
}

/** 選択肢 */
export const OptionForm = (props: OptionFormProps) => {
  const { name, option, disabled } = props
  
  const methods = useFormContext()
  const flagValue = useWatch({ name: `${name}.flag`, control: methods.control })
  
  return (
    <>
      <CheckBox 
        name={`${name}.flag`}
        label={option.optionName}
        valueAtCheck={yesNo.yes}
        disabled={disabled}
      >
        {option.optionName}
      </CheckBox>
      <GContainer>
        <GItem xs={0.3} />
        <GItem xs={11.7} >
          {flagValue === yesNo.yes && option.addQuestions && option.addQuestions.map((addQuestion) =>
            <GContainer key={`${name}.addQuestion.${addQuestion.fieldName}`}>
              <AddQuestionUnit
                topName={name}
                addQuestion={addQuestion}
                disabled={disabled}
              />
              
            </GContainer>
          )}
        </GItem>
      </GContainer>
    </>    
  )
}


interface MasterDataFormProps {
  name: string
  masterData: SpecifyMaster
  disabled: boolean
}

/** 選択肢 */
export const MasterDataForm = (props: MasterDataFormProps) => {
  const { name, masterData, disabled } = props
  
  const methods = useFormContext()
  const flagValue = useWatch({ name: `${name}.flag`, control: methods.control })
  
  return (
    <>
      <CheckBox 
        name={`${name}.flag`}
        label={masterData.name}
        valueAtCheck={yesNo.yes}
        disabled={disabled}
      >
        {masterData.name}
      </CheckBox>
      <GContainer>
        <GItem xs={0.3} />
        <GItem xs={11.7} >
          {flagValue === yesNo.yes && masterData.addQuestions && masterData.addQuestions.map((addQuestion) =>
            <GContainer key={`${name}.addQuestion.${addQuestion.fieldName}`}>
              <AddQuestionUnit
                topName={name}
                addQuestion={addQuestion}
                disabled={disabled}
              />
              
            </GContainer>
          )}
        </GItem>
      </GContainer>
    </>    
  )
}

interface AddQuestionUnitProps {
  topName: string
  addQuestion: AddQuestion
  disabled: boolean
}

export const AddQuestionUnit = (props: AddQuestionUnitProps) => {
  const { topName, addQuestion, disabled } = props
  
  const render = () => {
    switch(addQuestion?.fieldType){
      case fieldType.radio:
        const buttonValueLabels = addQuestion.addQuestionOptions?.map(op => ({
          label: op.optionText,
          value: op.optionValue,
        })) ?? []
      
        return(
          <RadioButtonGroup
            label={addQuestion.questionText}
            name={`${topName}.addAnswer.${addQuestion.fieldName}`}
            buttonValueLabels={buttonValueLabels}
            required={addQuestion.required === Flag.ON}
            row={addQuestion.direction === direction.horizontal}
            disabled={disabled}
          />
        )
      case fieldType.checkBox:
        return null // 未実装
        
      case fieldType.text:
        return (
          <TextBox 
            label={addQuestion.questionText}
            name={`${topName}.addAnswer.${addQuestion.fieldName}`}
            maxLength={addQuestion.maxLength}
            required={addQuestion.required === yesNo.yes}
            disabled={disabled}
          />
        )
        
      default:
        return null
    }
  }
  
  return (
    <>
      <GContainer>
        {addQuestion.required === yesNo.yes && <AttentionLabel>*</AttentionLabel> }
        {addQuestion.questionText}
      </GContainer>
      <GContainer>
        {render()}   
      </GContainer>
    </>
  )
}
